import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import Util from 'PreciateUtil';
import { ReplaySubject, takeUntil } from 'rxjs';

import { CustomValidators } from 'app/utils/custom.validators';
import { Loggable } from 'app/utils/logging/loggable';
import SocialEvent from 'entity/SocialEvent';
import QuestionInputType from 'enums/QuestionInputType';
import RegistrationStatus from 'enums/RegistrationStatus';

import { AccountSettingsFields } from '../../pages/registration-form-page/registration-form-page.component';
import { RegistrationFieldQuestions } from '../../services/events.service';
import { ModalRegistrantProfilePictureComponent } from '../modal-registrant-profile-picture/modal-registrant-profile-picture.component';

const EMAIL_REGEX = '^[eE]+[mM]+[aA]+[iI]+[lL]$';

export interface RegistrationFormEmiter {
  emailAddress?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  role?: string;
  linkedIn?: string;
  profileImageURI?: string;
  profileImageId?: string;
}

@Component({
  selector: 'app-registration-form',
  templateUrl: './registration-form.component.html',
  styleUrl: './registration-form.component.scss',
  standalone: false,
})
export class RegistrationFormComponent extends Loggable implements OnInit, OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);
  @Input() public registrationFields: RegistrationFieldQuestions[] = [];
  @Input() public isPreview = false;
  @Input() public event: SocialEvent = {};
  @Input() public embed = false;
  @Output() public formValue = new EventEmitter<RegistrationFormEmiter>();
  protected profilePictureTitle = AccountSettingsFields.PROFILE_PICTURE;
  protected profilePicture?: string;
  private profileImageId?: string;
  protected registrationForm?: UntypedFormGroup;
  protected timezone = '';
  protected questionInputType = QuestionInputType;

  public constructor(
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
  ) {
    super();
    this.timezone = Util.toTimezone(this.event.startDateTime || new Date());
  }

  public ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    const group: Record<string, UntypedFormControl> = {};
    this.registrationFields.forEach((question) => {
      if (!question.name) {
        return;
      }

      group[question.name] = question.required
        ? new UntypedFormControl('', [Validators.required, CustomValidators.maxByteLength(256)])
        : new UntypedFormControl('', [CustomValidators.maxByteLength(256)]);

      if (question.name.search(EMAIL_REGEX) === 0) {
        group[question.name].setValidators([
          Validators.email,
          Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-z]{2,4}$'),
          CustomValidators.maxByteLength(256),
        ]);
      }

      if (question.name === 'LinkedIn') {
        group[question.name].setValidators([
          Validators.pattern('^(http(s)?://)?([w]+.)?linkedin.com/(pub|in|profile)/([-a-zA-Z0-9]+)/*$'),
          CustomValidators.maxByteLength(256),
        ]);
      }
    });

    this.registrationForm = this.formBuilder.group(group);
  }

  protected getErrorMessage(field: string) {
    if (!this.registrationForm) {
      return '';
    }

    if (this.registrationForm.get(field)?.errors?.required) {
      return `${field} is required`;
    }

    if (this.registrationForm.get(field)?.errors?.maxByteLength) {
      return `Maximum character limit reached`;
    }

    if (field === 'LinkedIn') {
      return `Please add valid LinkedIn link`;
    }

    if (field.search(EMAIL_REGEX) === 0) {
      return `Please enter a valid email address`;
    }
    return '';
  }

  protected submitForm() {
    if (this.registrationForm?.valid) {
      const formValue: Record<string, string> = this.registrationForm.value;
      delete formValue[this.profilePictureTitle];

      const registrantValue = {
        ...formValue,
        profileImageId: this.profileImageId,
      };

      this.formValue.emit(registrantValue);
    }
  }

  protected openProfilePictureModal() {
    const dialogRef = this.dialog.open(ModalRegistrantProfilePictureComponent, {
      data: { currentImage: this.profilePicture, currentImageId: this.profileImageId },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((image) => {
        this.profilePicture = image?.base64;
        this.profileImageId = image?.imageId;
        this.registrationForm?.get(this.profilePictureTitle)?.setValue(image?.imageId);
      });
  }

  protected get showFields() {
    if (this.isPreview) {
      return true;
    }

    if (!this.event.endDateTime) {
      return false;
    }

    const hasDatePassed = (targetDate: Date) => {
      const currentDate = moment();
      const parsedTargetDate = moment(targetDate);
      return parsedTargetDate.isBefore(currentDate);
    };

    const eventDate = new Date(this.event.endDateTime);
    const isEventEnded = hasDatePassed(eventDate);

    if (
      this.event.registrationStatus === RegistrationStatus.LIVE &&
      this.registrationForm &&
      this.registrationFields.length &&
      !isEventEnded
    ) {
      return true;
    }

    return false;
  }

  protected get avatarLetters() {
    let letters = '';

    if (this.registrationForm?.get('First Name')?.value) {
      letters = this.registrationForm?.get('First Name')?.value.toUpperCase().substring(0, 1);
    }

    if (this.registrationForm?.get('Last Name')?.value) {
      letters += this.registrationForm?.get('Last Name')?.value.toUpperCase().substring(0, 1);
    }

    if (letters.length) {
      return letters;
    }

    return 'AB';
  }

  protected getTextAreaValueCount(field?: string): number {
    return this.registrationForm?.get(field ?? '')?.value.length ?? 0;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
